import { createUploadContentGate, UploadContentGate } from './uploadContentGate.ts';

/** A record the gate can name and link when an upload turns out to match content already stored. */
export interface MatchedDocument {
	id: number;
	publicId: string | null;
	filename: string;
}

/** The shape of a stored document this memory needs, structural so it is not tied to one query. */
export interface StoredDocument {
	id: number;
	filename: string;
	content_hash?: string | null;
	public_id?: string | null;
}

/** A document whose stored hash disagrees with the one the gate computed for the bytes it sent. */
export interface HashMismatch {
	documentId: number;
	sentHash: string;
	storedHash: string;
}

/**
 * What the client knows about a house's files for as long as the page is open.
 *
 * Held per house rather than per component: the composer mounts at several call sites and each one
 * builds its own upload hook, and every one of them reads the same memory.
 *
 * Rules about documents live here rather than in the hooks that read them, so the state and the
 * rule governing it stay together.
 */
export interface UploadSessionMemory {
	/**
	 * SHA-256 by `fileIdentity`. Content is fixed for a given name, size and mtime, so a file
	 * dropped again is never read twice.
	 */
	hashByFile: Map<string, string>;
	/** Which task each dropped file is riding on, so a re-drop finds the row that file already has. */
	taskIdByFile: Map<string, string>;
	/** Content already stored in the Bundle or already claimed by an upload on its way there. */
	contentGate: UploadContentGate;

	/**
	 * Takes in what the documents list currently holds.
	 *
	 * Belongs in render rather than an effect: the gate screens against this, and a task reaches
	 * `executeFn` from a zero-delay timer that can run before effects flush.
	 *
	 * Returns at once for a list it has already taken in, so repeat renders cost nothing.
	 */
	observeDocumentList(documents: readonly StoredDocument[]): void;

	/**
	 * The record holding this content, for a skip that can name what it matched. Only ever a
	 * document the user can see — the list it comes from is privacy-filtered.
	 */
	matchedDocument(hash: string): MatchedDocument | null;

	/**
	 * Whether a document was in the list and no longer is.
	 *
	 * Absence alone is not deletion: a document never seen is one the refetch has yet to reach.
	 */
	hasBeenDeleted(documentId: number): boolean;

	/** Notes the hash sent for a document this upload created, settled once the list shows it. */
	rememberSentHash(documentId: number, hash: string): void;

	/**
	 * Settles uploads against the list now showing them: retires each claim, leaving the list — the
	 * only one of the two that knows about deletion — to answer for that content, and checks each
	 * stored hash against the one sent.
	 *
	 * Returns disagreements rather than logging them, so reporting stays with the caller.
	 */
	confirmUploads(documents: readonly StoredDocument[]): HashMismatch[];
}

const memoryByHouse = new Map<number, UploadSessionMemory>();

const createSessionMemory = (): UploadSessionMemory => {
	let knownDocumentsByHash: ReadonlyMap<string, MatchedDocument> = new Map();
	let storedDocumentIds: ReadonlySet<number> = new Set();
	let lastObserved: readonly StoredDocument[] | null = null;
	const seenDocumentIds = new Set<number>();
	const sentHashByDocumentId = new Map<number, string>();
	const contentGate = createUploadContentGate(() => knownDocumentsByHash);

	return {
		hashByFile: new Map(),
		taskIdByFile: new Map(),
		contentGate,

		observeDocumentList(documents) {
			if (documents === lastObserved) return;
			lastObserved = documents;

			const byHash = new Map<string, MatchedDocument>();
			const ids = new Set<number>();
			for (const document of documents) {
				ids.add(document.id);
				seenDocumentIds.add(document.id);
				// First wins: two records can hold the same content, and either names it equally well.
				if (document.content_hash && !byHash.has(document.content_hash)) {
					byHash.set(document.content_hash, {
						id: document.id,
						publicId: document.public_id ?? null,
						filename: document.filename,
					});
				}
			}
			knownDocumentsByHash = byHash;
			storedDocumentIds = ids;
		},

		matchedDocument(hash) {
			return knownDocumentsByHash.get(hash) ?? null;
		},

		hasBeenDeleted(documentId) {
			return seenDocumentIds.has(documentId) && !storedDocumentIds.has(documentId);
		},

		rememberSentHash(documentId, hash) {
			sentHashByDocumentId.set(documentId, hash);
		},

		confirmUploads(documents) {
			if (sentHashByDocumentId.size === 0) return [];

			const mismatches: HashMismatch[] = [];
			for (const document of documents) {
				const sentHash = sentHashByDocumentId.get(document.id);
				if (sentHash === undefined) continue;
				sentHashByDocumentId.delete(document.id);
				contentGate.retire(sentHash);

				const storedHash = document.content_hash;
				if (storedHash && storedHash !== sentHash) {
					mismatches.push({ documentId: document.id, sentHash, storedHash });
				}
			}
			return mismatches;
		},
	};
};

/**
 * The house's memory, created on first use. Keyed by house so switching between two properties
 * keeps each one's hashes and claims rather than discarding both.
 */
export const getUploadSessionMemory = (houseId: number): UploadSessionMemory => {
	const existing = memoryByHouse.get(houseId);
	if (existing) return existing;

	const created = createSessionMemory();
	memoryByHouse.set(houseId, created);
	return created;
};

/** Module state outlives a test, so each one starts from a house that has seen nothing. */
export const forgetUploadSessionMemory = (): void => memoryByHouse.clear();
